<script setup>
import { computed, inject } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import FrontendLogo from "@/Components/FrontendLogo.vue";
import ContactUs from "@/Components/Web/ContactUs.vue";
import { useI18n } from "vue-i18n";

const route = inject("route");
const { t } = useI18n();
const page = usePage();
const apps = computed(() => page.props.apps || {});
const year = new Date().getFullYear();

const socials = computed(() => [
  { key: "fb", href: apps.value.linkfb, title: "Facebook" },
  { key: "ig", href: apps.value.linkinstagram, title: "Instagram" },
  { key: "yt", href: apps.value.linkyoutube, title: "YouTube" },
  {
    key: "email",
    href: apps.value.linkemail ? `mailto:${apps.value.linkemail}` : null,
    title: "Email",
  },
  { key: "web", href: apps.value.linkweb, title: "Website" },
]);
</script>

<template>
  <footer class="relative z-10 bg-white border-t border-gray-200">



    <!-- ===== Brand + address (centered) ===== -->
    <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 py-8 text-center">
      <Link :href="route('home')" class="inline-flex items-center justify-center">
        <FrontendLogo class="w-10 h-10 text-primary" />
      </Link>

      <p class="mt-4 text-sm leading-relaxed text-center text-gray-500">
        {{
          apps.deskripsi ||
          "Portal resmi yang menyajikan transparansi, akuntabilitas, dan informasi terkini."
        }}
      </p>

      <!-- Address -->
      <p class="flex items-start justify-center max-w-xl gap-2 mx-auto mt-2 text-sm leading-relaxed text-gray-600">
        <i class="mt-0.5 fa-solid fa-location-dot text-primary shrink-0"></i>
        <span>
          Kampus I Universitas Negeri Gorontalo, Jl. Jend. Sudirman No. 6, Kota
          Gorontalo, Provinsi Gorontalo
        </span>
      </p>

      <!-- Social -->
      <div class="flex items-center justify-center gap-3 mt-6">
        <template v-for="s in socials" :key="s.key">
          <a v-if="s.href" :href="s.href" :target="s.key === 'email' ? undefined : '_blank'" rel="noopener noreferrer"
            class="flex items-center justify-center w-10 h-10 text-gray-500 transition-colors duration-200 bg-white border border-gray-200 rounded-full hover:text-white hover:bg-primary hover:border-primary"
            :title="s.title">
            <svg v-if="s.key === 'fb'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            <svg v-else-if="s.key === 'ig'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-2.504 0-4.536-2.033-4.536-4.537s2.032-4.536 4.536-4.536 4.537 2.032 4.537 4.536-2.033 4.537-4.537 4.537zm7.119 0c-2.504 0-4.536-2.033-4.536-4.537s2.032-4.536 4.536-4.536 4.537 2.032 4.537 4.536-2.033 4.537-4.537 4.537z" />
            </svg>
            <svg v-else-if="s.key === 'yt'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            <svg v-else-if="s.key === 'email'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
            <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
            </svg>
          </a>
        </template>
      </div>
    </div>

    <!-- ===== Bottom bar ===== -->
    <div class="border-t border-gray-200">
      <div
        class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 py-5 flex flex-col items-center justify-between gap-3 sm:flex-row">
        <p class="text-sm text-gray-500">
          &copy; {{ year }} {{ apps.bussinesname }}
        </p>
        <p class="text-sm text-gray-500">
          &nbsp;
          <a href="https://www.facebook.com/anselmus" target="_blank" rel="noopener noreferrer"
            class="font-medium transition-colors duration-200 hover:text-primary">
            Tim Humas FEB
          </a>
        </p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.contact-us-wrapper {
  max-width: 100%;
  overflow: hidden;
}
</style>
